import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Navigation from '../components/Navigation.jsx';
// v1.17 - Updated title to "Therapy Room" and fixed PDF download

function LeadMagnetPage() {
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState('');
  const [scrolled, setScrolled] = useState(false);

  // Scroll listener for transparent navigation
  React.useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  const handleSubmit = async e => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address');
      setIsSubmitting(false);
      return;
    }
    if (!firstName.trim()) {
      setError('Please enter your first name');
      setIsSubmitting(false);
      return;
    }
    try {
      // ⚠️ MAILCHIMP SETUP INSTRUCTIONS:
      // 
      // STEP 1: Create your Mailchimp account (free up to 500 subscribers)
      // Go to: https://mailchimp.com and sign up
      //
      // STEP 2: Create an Audience (email list)
      // Click "Audience" → "Audience dashboard" → Create audience
      //
      // STEP 3: Get your Mailchimp Form URL
      // 1. Click "Audience" → "Signup forms" → "Embedded forms"
      // 2. Look for the <form action="..."> tag in the code
      // 3. Copy the FULL URL from action="..." 
      // Example: https://yourname.us1.list-manage.com/subscribe/post?u=abc123&id=xyz789
      //
      // STEP 4: Replace YOUR_MAILCHIMP_URL_HERE below with your URL
      //
      // STEP 5: Test your form!
      //
      // For detailed setup instructions, see: MAILCHIMP_SETUP_GUIDE.md

      // ========== MAILCHIMP INTEGRATION ==========
      const MAILCHIMP_URL = 'YOUR_MAILCHIMP_URL_HERE';

      // If you haven't set up Mailchimp yet, the form will still work
      // and redirect to thank-you page. You just won't collect emails yet.
      if (MAILCHIMP_URL !== 'YOUR_MAILCHIMP_URL_HERE') {
        // Convert to JSONP endpoint to avoid CORS issues
        const jsonpUrl = MAILCHIMP_URL.replace('/post?', '/post-json?') + `&EMAIL=${encodeURIComponent(email)}&FNAME=${encodeURIComponent(firstName)}`;

        // Submit to Mailchimp
        await fetch(jsonpUrl, {
          method: 'GET',
          mode: 'no-cors'
        });
      } else {
        // Mailchimp not set up yet - log to console for testing
        console.log('⚠️ Mailchimp not configured yet. Form submitted with:', {
          email,
          firstName
        });
        console.log('📧 To collect emails, follow setup instructions in: MAILCHIMP_SETUP_GUIDE.md');
      }

      // Show success message
      setIsSuccess(true);

      // Track conversion in Google Analytics
      if (window.gtag) {
        window.gtag('event', 'generate_lead', {
          event_category: 'Lead Magnet',
          event_label: 'People-Pleasing Therapy Room Guide'
        });
      }

      // Redirect to thank you page after 1.5 seconds
      setTimeout(() => {
        window.location.href = '/thank-you';
      }, 1500);
    } catch (err) {
      console.error('Form submission error:', err);
      setError('Something went wrong. Please try again.');
    }
    setIsSubmitting(false);
  };
  const handleDownload = () => {
    // ⚠️ PDF FILE SETUP REQUIRED:
    // 1. Create your PDF guide
    // 2. Upload it to a hosting service (Google Drive, Dropbox, ConvertKit, etc.)
    // 3. Get a direct download link
    // 4. Replace the URL below with your PDF link

    const pdfUrl = 'YOUR_PDF_URL_HERE'; // Replace with your actual PDF URL

    if (pdfUrl === 'YOUR_PDF_URL_HERE') {
      alert('⚠️ PDF Setup Required\n\nPlease upload your PDF and update the download link in:\nsrc/pages/LeadMagnetPage.jsx (line 65)\n\nSee PDF_GUIDE_CREATION.md for instructions.');
      return;
    }
    window.open(pdfUrl, '_blank');
    setTimeout(() => {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }, 1000);
  };
  return <div className="min-h-screen bg-gradient-to-b from-warm-50 to-white">
      <Navigation variant="transparent-to-solid" scrolled={scrolled} />
      {/* Hero Section - Full Width Banner */}
      <section className="relative w-full">
        <div className="relative w-full h-[480px] md:h-[560px] lg:h-[640px]">
          {/* Background Image with Overlay - Therapy Session */}
          <img src="https://res.cloudinary.com/makerinc/image/fetch/c_limit,w_1200,f_auto,q_auto:best,fl_preserve_transparency,dpr_2/https%3A%2F%2Fmaker-devi-dev.s3-accelerate.amazonaws.com%2Fuploads%2Fuser_33tbl7B6e1bXN6KW4wjrevvqrth%2Fcmhd1gtfc0001vb0yeeofdn9z%2Fmessages%2Fback-of-therapist-1-zfcyv7c7wq.jpg" alt="Therapy session" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/70"></div>
        
          {/* Text Content Overlay */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">


              <h1 className="text-4xl leading-10 leading-10 md:text-5xl md:leading-none md:leading-none lg:text-6xl lg:leading-none lg:leading-none [font-family:Cormorant_Garamond,serif] font-light leading-tight text-white [filter:drop-shadow(0_25px_25px_rgb(0,0,0,0.15))] mt-[215px] mb-3 ml-[50px] mr-[50px] pt-0 pb-0 pl-0 pr-0">
                People-Pleasing in<br />the Therapy Room
              </h1>
              
              <p className="text-xl md:text-2xl text-white mb-4 font-light drop-shadow-lg">
                A guide on how to address power dynamics with your therapist
              </p>

              <p className="text-lg text-white max-w-2xl mx-auto drop-shadow-lg"></p>
            </div>
          </div>

          {/* Scroll Arrow */}
          <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-10 animate-float">
            <div className="bg-white bg-opacity-20 backdrop-blur-sm rounded-full p-3 hover:scale-110 transition-transform duration-300 cursor-pointer">
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
              </svg>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
        <div className="grid lg:grid-cols-2 gap-12 items-start">
          {/* Left Column - What's Inside */}
          <div>
            <h2 className="text-3xl font-serif text-sage-800 mb-6">
              What You'll Learn
            </h2>

            <div className="space-y-6 mb-8">
              <div className="flex items-start">
                <div className="flex-shrink-0 w-12 h-12 bg-warm-100 rounded-lg flex items-center justify-center mr-4">
                  <svg className="w-6 h-6 text-warm-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">
                    How Power Dynamics Shape Therapy
                  </h3>
                  <p className="text-gray-600">
                    Understand the inherent power imbalance in the therapeutic relationship and how it can trigger people-pleasing patterns.
                  </p>
                </div>
              </div>

              <div className="flex items-start">
                <div className="flex-shrink-0 w-12 h-12 bg-warm-100 rounded-lg flex items-center justify-center mr-4">
                  <svg className="w-6 h-6 text-warm-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">
                    Intersectional Identities Matter
                  </h3>
                  <p className="text-gray-600">
                    Learn how race, gender, class, and other identities influence the therapy dynamic and your comfort level.
                  </p>
                </div>
              </div>

              <div className="flex items-start">
                <div className="flex-shrink-0 w-12 h-12 bg-warm-100 rounded-lg flex items-center justify-center mr-4">
                  <svg className="w-6 h-6 text-warm-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">
                    What Culturally Humble Therapists Do
                  </h3>
                  <p className="text-gray-600">
                    Identify the qualities and practices that make therapy truly collaborative and empowering.
                  </p>
                </div>
              </div>

              <div className="flex items-start">
                <div className="flex-shrink-0 w-12 h-12 bg-warm-100 rounded-lg flex items-center justify-center mr-4">
                  <svg className="w-6 h-6 text-warm-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">
                    Questions to Ask Potential Therapists
                  </h3>
                  <p className="text-gray-600">
                    Get a practical list of questions to help you find a therapist who respects your needs and boundaries.
                  </p>
                </div>
              </div>

              {/* Special Section for Professionals */}
              <div className="flex items-start mt-8 pt-8 border-t border-warm-200">
                <div className="flex-shrink-0 w-12 h-12 bg-sage-100 rounded-lg flex items-center justify-center mr-4">
                  <svg className="w-6 h-6 text-sage-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">
                    Special Section for Professionals
                  </h3>
                  <p className="text-gray-600">
                    <span className="font-semibold text-sage-700">For therapists, coaches, and other professionals:</span> I share my two cents on how to maintain safety in your client relationships and avoid possible retraumatization.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Coming Soon Preview Box */}
          <div className="lg:sticky lg:top-24">
            <div className="bg-white rounded-2xl shadow-xl border-2 border-warm-200 overflow-hidden">
              {/* Preview Header */}
              <div className="bg-gradient-to-br from-warm-500 to-sage-500 p-8 text-white text-center">
                <div className="w-16 h-16 bg-white bg-opacity-20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                </div>
                <h3 className="text-2xl font-serif font-light mb-2">
                  Free Guide Preview
                </h3>
                <p className="text-white text-opacity-90"></p>
              </div>

              {/* Preview Content */}
              <div className="p-8">
                <div className="space-y-5">
                  {/* Preview Notice */}
                  <div className="bg-amber-50 border-2 border-amber-200 rounded-lg px-4 py-4 text-center">
                    <p className="text-amber-900 font-semibold mb-2"></p>
                    <p className="text-sm text-amber-800">The guide is almost done... getting all dressed up and ready!</p>
                  </div>

                  {/* Disabled Button */}
                  <button disabled className="w-full bg-gray-300 text-gray-500 py-4 rounded-lg font-semibold cursor-not-allowed opacity-60">
                    Free Guide Coming Soon
                  </button>

                  <p className="text-xs text-gray-500 text-center mt-2"></p>
                </div>
              </div>

              {/* Preview Features */}
              <div className="px-8 pb-8 pt-4 border-t border-gray-100">
                <div className="flex items-center justify-center space-x-6 text-xs text-gray-400">
                  <div className="flex items-center">
                    <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    Secure
                  </div>
                  <div className="flex items-center">
                    <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                      <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                    </svg>
                    No Spam
                  </div>
                  <div className="flex items-center">
                    <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    Free Forever
                  </div>
                </div>
              </div>

              {!isSuccess ? null : <div className="p-12 text-center">
                  {/* Success State */}
                  <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <svg className="w-10 h-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  
                  <h3 className="text-2xl font-serif text-gray-800 mb-3">
                    Success, {firstName}!
                  </h3>
                  <p className="text-gray-600 mb-8 text-lg">
                    Your free guide is ready to download.
                  </p>

                  <button onClick={handleDownload} className="w-full bg-gradient-to-r from-warm-500 to-sage-500 text-white py-4 rounded-lg font-semibold hover:from-warm-600 hover:to-sage-600 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105 mb-4">
                    📥 Download PDF Now
                  </button>

                  <p className="text-sm text-gray-500 mb-6">
                    Check your email for a copy of the download link!
                  </p>

                  <Link to="/#booking" className="inline-block text-sage-600 hover:text-sage-700 font-medium text-sm underline"><span>
                    Ready to work together? Book a consultation →
                  </span></Link>
                </div>}
            </div>

            {/* Below Form CTA */}
          </div>
        </div>

        {/* Why This Guide Matters Section - Moved before story */}
        <div className="mt-20 pt-16 border-t border-gray-200">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-serif text-sage-800 mb-6">
              Why This Guide Matters
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed mb-6">
              If you've struggled with people-pleasing your whole life, it doesn't magically stop when you walk into a therapist's office. In fact, the power dynamics inherent in therapy can make it worse.
            </p>
            <p className="text-lg text-gray-600 leading-relaxed mb-8">{"This guide will help you recognize when you're people-pleasing with your therapist, understand how cultural dynamics play a role, and find a therapist who creates genuine safety, not just surface-level comfort."}</p>
            
            <Link to="/about" className="inline-flex items-center text-sage-600 hover:text-sage-700 font-medium"><span>
              Learn more about my approach
              </span><svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
          </div>
        </div>

        {/* Author's Story - Full Width Section */}
        <div className="mt-20 pt-16 border-t border-gray-200">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Story Box - Similar to Homepage Testimonial Style */}
            <div className="bg-white rounded-3xl shadow-xl p-8 md:p-12 relative">
              {/* Quote Icon */}
              <svg className="w-16 h-16 text-warm-200 absolute top-8 left-8 opacity-50" fill="currentColor" viewBox="0 0 24 24">
                <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
              </svg>

              <div className="relative z-10">
                <p className="text-lg md:text-xl text-gray-700 leading-relaxed mb-4">{"I spent years, and thousands of dollars in therapy, trying to understand why I couldn't stop people-pleasing. No matter how much I learned, I still found myself shrinking, smoothing things over, and staying small."}</p>
                
                <p className="text-lg md:text-xl text-gray-700 leading-relaxed mb-4">
                  My nervous system was just doing what it knew best: <span className="font-semibold text-sage-700">staying safe by seeking approval and not taking up too much space.</span>
                </p>
                
                <p className="text-lg md:text-xl text-gray-700 leading-relaxed mb-4">
                  But <span className="font-semibold text-sage-700">healing doesn't happen when we're performing. It happens when we feel safe enough to be real.</span>
                </p>
                
                <p className="text-lg md:text-xl text-gray-700 leading-relaxed mb-4">{"For a long time, I didn't feel that kind of safety. Even in therapy, I caught myself fawning: agreeing, pleasing, and silencing my truth. My therapist cared deeply, but they didn't know how to help me feel safe as me, how to name and tend to the power differences that quietly shaped our relationship."}</p>
                
                <p className="text-lg md:text-xl text-gray-700 leading-relaxed mb-4">{"As an immigrant, I felt unseen in a space that often valued white, individualistic ways of being over my relational, communal ones. I wanted healing, but I also needed to feel understood in the fullness of who I am."}</p>
                
                <p className="text-lg md:text-xl text-gray-700 leading-relaxed mb-4">{"Things began to shift when I started working with a therapist who was a trained trauma expert using somatic therapy practices. They were intentional about creating safety and gently softening the hierarchy between us. That changed everything. For the first time, my body could settle. I could stop performing. I could experiment with taking up more space, and just being myself in the presence of another."}</p>
                
                <p className="text-lg md:text-xl text-gray-700 leading-relaxed">{"This guide is born from that journey, the frustration, the searching, and the deep relief of finally discovering what safety truly feels like. I wrote it for others like me, and for the healers and therapists who hold space for others' pain. Because real healing happens when power is shared, and when every culture, body, and voice is honored as equally worthy and welcome."}</p>
                
                <div className="mt-8 flex items-center gap-4">
                  <div className="w-14 h-14 rounded-full bg-gradient-to-br from-sage-400 to-warm-400 flex items-center justify-center text-white text-xl font-semibold">
                    Y
                  </div>
                  <div>
                    <p className="font-semibold text-sage-800 text-lg">
                      Yasi Philipos
                    </p>
                    <p className="text-gray-600">Client and Licensed Therapist</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Therapy Session Image - Full Width Section */}
        <div className="mt-20 pt-16">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="relative w-full h-[400px] md:h-[500px] lg:h-[550px] rounded-3xl overflow-hidden shadow-2xl hover:shadow-3xl transition-all duration-500 group">
              <img src="https://res.cloudinary.com/makerinc/image/fetch/c_limit,w_1200,f_auto,q_auto:best,fl_preserve_transparency,dpr_2/https%3A%2F%2Fmaker-devi-dev.s3-accelerate.amazonaws.com%2Fuploads%2Fuser_33tbl7B6e1bXN6KW4wjrevvqrth%2Fcmhd1gtfc0001vb0yeeofdn9z%2Fmessages%2Ffree-guide-1-ne29dp37yol.jpg" alt="Therapist and client in a supportive conversation" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" style={{
              objectPosition: 'center 65%'
            }} />
            </div>
          </div>
        </div>
      </div>
    </div>;
}
export default LeadMagnetPage;